/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    CircularProgress,
    Box,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Popover,
    Typography,
    IconButton
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styles from "./alertTables.module.css";
import vector from "../../../../assets/images/Vector.svg";
import ExpandRight from "../../../../assets/images/expandRight.svg";
import AlertPagination from "./AlertPagination";
import { formatText } from "utils/formatText";
import CustomTooltip from "utils/CustomTooltip";
import { StyledTableCell, StyledTableRow, StyledLeftEmptyCell, StyledEmptyCell, StyledTableSubHeaderCell } from "./materialUITableStyling";
import {columnDesc} from "../../../../utils/columndesc";

const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];

const NormAlertTable = ({ data, columnList, type, viewType, paginationObj, onPageChange, loading, perWeekMai, subtotalFgCoverage, subtotalPhysicalCoverage, weeklySubtotals }) => {
    const [localItems, setLocalItems] = useState(data);
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const percentageMsg = 'Weekly OR' === type ? { "red": "< 80%", "yellow": "80% - 90%", "green": "90% - 100%" } : { "red": "< 80%", "yellow": "80% - 94%", "green": "> 95%" };
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [expandedColumns, setExpandedColumns] = useState({});
    const [columnInfoAnchor, setColumnInfoAnchor] = useState(null);
    const [columnInfoText, setColumnInfoText] = useState("");

    useEffect(() => {
        setLocalItems(data);
        if (!paginationObj && !onPageChange) {
            setPage(0);
        }
    }, [data]);

    const sortedData = useMemo(() => {
        if (sortConfig.key) {
            return [...localItems].sort((a, b) => {
                const valueA = a[sortConfig.key];
                const valueB = b[sortConfig.key];

                if (!isNaN(valueA) && !isNaN(valueB)) {
                    return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
                }
                if (typeof valueA === "string" && typeof valueB === "string") {
                    return sortConfig.direction === "asc"
                        ? valueA.localeCompare(valueB)
                        : valueB.localeCompare(valueA);
                }
                return 0;
            });
        }

        return localItems;
    }, [localItems, sortConfig, type, viewType]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    // Toggle column expansion
    const toggleColumnExpansion = (columnLabel, e) => {
        e.stopPropagation(); // Prevent triggering sort
        setExpandedColumns(prev => ({
            ...prev,
            [columnLabel]: !prev[columnLabel]
        }));
    };

    // Check if column can be expanded/collapsed for width
    const isTruncatableColumn = (label) => {
        return label === "CBU description";
    };

    const handleChangePage = (event, newPage, pageSize = rowsPerPage) => {
        if (!paginationObj && !onPageChange) {
            setPage(newPage)
        } else {
            const actualPage = newPage + 1;
            if (onPageChange) {
                onPageChange(actualPage, pageSize);
            }
        }
    };

    const handleChangeRowsPerPage = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);

        if (!paginationObj && !onPageChange) {
            setPage(0);
        } else if (onPageChange) {
            onPageChange(1, newRowsPerPage);
        }
    };

    const selectedColumns = columnList.filter(a => a.visibility === true);
    const displayColumn = useMemo(() => {
        if (type === "Norm Alert") return "% Stock to norm coverage";
        if (type === "Weekly OR") return "Weekly OR";
        if (type === "FG Coverage") return "Total FG Coverage";
        return "";
    }, [type]);

    const weekKeys = [
        ...new Set(
            localItems.flatMap(item => {
                if (type === "FG Coverage") {
                    return Object.keys(item["Total FG Coverage"] || {}).filter(key => key.startsWith("Wk"));
                }

                return Object.keys(item[displayColumn] || {}).filter(key => key.startsWith("Wk"));
            })
        )
    ];

    const weekPhysicalKeys = type === "FG Coverage"
        ? [...new Set(
            localItems.flatMap(item =>
                Object.keys(item["Physical FG Coverage"] || {}).filter(key => key.startsWith("Wk"))
            )
        )]
        : [];

    const getRowKey = (alert, index) => {
        const parts = [
            alert?.["Alert ID"],
            alert?.["Material ID"],
            alert?.["Material Desc"],
            alert?.["Site code"],
            alert?.["CBU code"],
            alert?.["CBU"],
            alert?.["Basepack code"]
        ].filter(Boolean);
        if (parts.length > 0) {
            return `${parts.join("-")}-${index}`;
        }

        return `fg-row-${index}`;
    };

    const hasFgCoverageSubtotals = useMemo(
        () => Object.keys(subtotalFgCoverage || {}).length > 0 || Object.keys(subtotalPhysicalCoverage || {}).length > 0,
        [subtotalFgCoverage, subtotalPhysicalCoverage]
    );

    const getDataSlice = () => (paginationObj || onPageChange
        ? sortedData
        : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));

    const renderNormStockCellContent = (stockValue, tooltipContent) => {
        if (type !== 'Norm Alert') {
            return stockValue;
        }

        return (
            <Tooltip
                title={tooltipContent}
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            padding: 0,
                        },
                    },
                }}
            >
                <span style={{ display: 'block', width: '100%', height: '100%' }}>{stockValue}</span>
            </Tooltip>
        );
    };

    // Helper to get background color for parent-child sets
    const getParentChildColor = (setIndex) => {
        // Odd sets get dark blue, even sets get light blue
        return setIndex % 2 === 1 ? '#C2E0FF' : '#354ACD';
    };

    const getTextColor = (setIndex) => {
        return setIndex % 2 === 1 ? '#0B1F44' : '#FFFFFF';
    };


    const columnDescriptionByNormalizedLabel = useMemo(() => {
        const map = new Map();
        Object.entries(columnDesc[type] || {}).forEach(([label, desc]) => {
            map.set(label, desc);
        });
        return map;
    }, [columnDesc]);

    const getColumnDescription = useCallback((label) => (
        columnDescriptionByNormalizedLabel.get(label) || ""
    ), [columnDescriptionByNormalizedLabel]);

    const handleOpenColumnInfo = (event, label) => {
        event.stopPropagation();
        const description = getColumnDescription(label);
        if (!description) {
            return;
        }
        setColumnInfoText(description);
        setColumnInfoAnchor(event.currentTarget);
    };

    const handleCloseColumnInfo = () => {
        setColumnInfoAnchor(null);
        setColumnInfoText("");
    };


    const renderMainHeader = () => {
        if (type === "FG Coverage") {
            return (
                <TableRow>
                    {selectedColumns
                        .filter(col => !["Physical FG Coverage", "Total FG Coverage"].includes(col.label))
                        .map((col) => (
                            <StyledTableCell
                                key={`main-col-${col.label}`}
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    minWidth: expandedColumns[col.label] ? '300px' : undefined,
                                    maxWidth: expandedColumns[col.label] ? '300px' : undefined,
                                    width: expandedColumns[col.label] ? '300px' : undefined,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                    <span style={{ flex: 1 }}>{col.label}</span>
                                    {Boolean(getColumnDescription(col.label)) && (
                                        <IconButton
                                            size="small"
                                            aria-label={`${col.label} info`}
                                            onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                            sx={{ padding: '1px', color: '#ffffff' }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    )}
                                    {isTruncatableColumn(col.label) && (
                                        <button
                                            type="button"
                                            onClick={(e) => toggleColumnExpansion(col.label, e)}
                                            title="Expand/Collapse column"
                                            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0, border: "none", background: "transparent", padding: 0 }}
                                        >
                                            <img src={ExpandRight} alt="expandRight" />
                                        </button>
                                    )}
                                </div>
                            </StyledTableCell>
                        ))}
                    <StyledTableCell
                        key="physical-fg-header"
                        colSpan={(weekPhysicalKeys.length || 1) * 1}
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            backgroundColor: getParentChildColor(1) + ' !important',
                            color: getTextColor(1) + ' !important',
                            border: getParentChildColor(1) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                        }}
                    >
                        Physical FG Coverage
                        {Boolean(getColumnDescription("Physical FG Coverage")) && (
                            <IconButton
                                size="small"
                                aria-label={`Physical FG Coverage info`}
                                onClick={(event) => handleOpenColumnInfo(event, "Physical FG Coverage")}
                                sx={{ padding: '1px', color: '#ffffff' }}
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                        )}
                    </StyledTableCell>
                    <StyledTableCell
                        key="weekly-fg-header"
                        colSpan={(weekKeys.length || 1) * 1}
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            backgroundColor: getParentChildColor(2) + ' !important',
                            color: getTextColor(2) + ' !important',
                            border: getParentChildColor(2) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                        }}
                    >
                        Total FG Coverage
                        {Boolean(getColumnDescription("Total FG Coverage")) && (
                            <IconButton
                                size="small"
                                aria-label={`Total FG Coverage info`}
                                onClick={(event) => handleOpenColumnInfo(event, "Total FG Coverage")}
                                sx={{ padding: '1px', color: '#ffffff' }}
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                        )}
                    </StyledTableCell>
                </TableRow>
            );
        }

        // For RMPM Alert MAI View and Norm Alert
        return (
            <TableRow>
                {selectedColumns.map((col) => {
                    // Skip if this is our special column (it will be handled separately)
                    if (col.label === displayColumn) {
                        return null;
                    }
                    return (
                        <StyledTableCell
                            key={`main-col-${col.label}`}
                            sx={{
                                cursor: col.sortable ? "pointer" : "default",
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                minWidth: expandedColumns[col.label] ? '250px' : undefined,
                                maxWidth: expandedColumns[col.label] ? '250px' : undefined,
                                width: expandedColumns[col.label] ? '250px' : undefined,
                            }}
                        >
                            <div className={styles.sortIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                <button
                                    type="button"
                                    onClick={() => col.sortable && handleSort(col.label)}
                                    style={{
                                        flex: 1,
                                        border: "none",
                                        background: "transparent",
                                        padding: 0,
                                        margin: 0,
                                        textAlign: "left",
                                        cursor: col.sortable ? "pointer" : "default",
                                        font: "inherit",
                                        color: "inherit"
                                    }}
                                >
                                    {col.label}
                                    {Boolean(getColumnDescription(col.label)) && (
                                        <IconButton
                                            size="small"
                                            aria-label={`${col.label} info`}
                                            onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                            sx={{ padding: '1px', color: '#ffffff' }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    )}
                                </button>
                                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                    {col.sortable && sortConfig.key === col.label && (
                                        <img
                                            src={vector}
                                            alt="sort icon"
                                            width="14px"
                                            height="14px"
                                            style={{ marginLeft: "4px" }}
                                        />
                                    )}
                                    {isTruncatableColumn(col.label) && (
                                        <button
                                            type="button"
                                            onClick={(e) => toggleColumnExpansion(col.label, e)}
                                            title="Expand/Collapse column"
                                            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", border: "none", background: "transparent", padding: 0 }}
                                        >
                                            <img src={ExpandRight} alt="expandRight" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </StyledTableCell>
                    );
                })}
                <StyledTableCell
                    key={`main-header-${displayColumn}`}
                    colSpan={weekKeys.length}
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: getParentChildColor(1) + ' !important',
                        color: getTextColor(1) + ' !important',
                        border: getParentChildColor(1) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                    }}
                >
                    {displayColumn}
                    {Boolean(getColumnDescription(displayColumn)) && (
                        <IconButton
                            size="small"
                            aria-label={`${displayColumn} info`}
                            onClick={(event) => handleOpenColumnInfo(event, displayColumn)}
                            sx={{ padding: '1px', color: '#ffffff' }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    )}
                </StyledTableCell>
            </TableRow>
        );
    };

    const renderWeekHeaders = () => {
        if (type === "FG Coverage") {
            return (
                <>
                    <TableRow>
                        {selectedColumns
                            .filter(col => !["Physical FG Coverage", "Total FG Coverage"].includes(col.label))
                            .map((col, index) => (
                                <StyledTableSubHeaderCell
                                    key={`sub-header-${col.label}`}
                                    sx={{
                                        ...(index === 0 ? StyledLeftEmptyCell : StyledEmptyCell),
                                        position: 'sticky',
                                        top: '36px',
                                        zIndex: 1
                                    }}
                                />
                            ))}
                        {weekPhysicalKeys.map((week) => (
                            <StyledTableSubHeaderCell
                                key={`physical-week-${week}`}
                                colSpan={1}
                                sx={{
                                    position: 'sticky',
                                    top: '36px',
                                    zIndex: 1,
                                    backgroundColor: getParentChildColor(1) + ' !important',
                                    color: getTextColor(1) + ' !important',
                                    border: getParentChildColor(1) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                                }}
                            >
                                {week}
                            </StyledTableSubHeaderCell>
                        ))}
                        {weekKeys.map((week) => (
                            <StyledTableSubHeaderCell
                                key={`weekly-week-${week}`}
                                colSpan={1}
                                sx={{
                                    position: 'sticky',
                                    top: '36px',
                                    zIndex: 1,
                                    backgroundColor: getParentChildColor(2) + ' !important',
                                    color: getTextColor(2) + ' !important',
                                    border: getParentChildColor(2) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                                }}
                            >
                                {week}
                            </StyledTableSubHeaderCell>
                        ))}
                    </TableRow>
                    {hasFgCoverageSubtotals && (
                        <TableRow>
                            {selectedColumns
                                .filter(col => !["Physical FG Coverage", "Total FG Coverage"].includes(col.label))
                                .map((col, index) => (
                                    <StyledTableSubHeaderCell
                                        key={`subtotal-header-${col.label}`}
                                        sx={{
                                            ...(index === 0 ? StyledLeftEmptyCell : StyledEmptyCell),
                                            position: 'sticky',
                                            top: '64px',
                                            zIndex: 1
                                        }}
                                    >
                                    </StyledTableSubHeaderCell>
                                ))}
                            {weekPhysicalKeys.map((week) => {
                                const matchingKey = Object.keys(subtotalPhysicalCoverage || {}).find(
                                    key => key.replace(/\s+/g, '') === week.replace(/\s+/g, '')
                                );
                                const weekData = matchingKey ? subtotalPhysicalCoverage[matchingKey] : null;
                                const percentage = weekData?.Percentage ?? "-";
                                return (
                                    <StyledTableSubHeaderCell
                                        key={`physical-subtotal-perc-${week}`}
                                        stockcoverage={percentage}
                                        type="Physical FG Coverage"
                                        sx={{
                                            position: 'sticky',
                                            top: '64px',
                                            zIndex: 1
                                        }}
                                    >
                                        {percentage}
                                    </StyledTableSubHeaderCell>
                                );
                            })}
                            {weekKeys.map((week) => {
                                const matchingKey = Object.keys(subtotalFgCoverage || {}).find(
                                    key => key.replace(/\s+/g, '') === week.replace(/\s+/g, '')
                                );
                                const weekData = matchingKey ? subtotalFgCoverage[matchingKey] : null;
                                const percentage = weekData?.Percentage ?? "-";
                                return (
                                    <StyledTableSubHeaderCell
                                        key={`weekly-subtotal-perc-${week}`}
                                        stockcoverage={percentage}
                                        type="FG Coverage"
                                        sx={{
                                            position: 'sticky',
                                            top: '64px',
                                            zIndex: 1
                                        }}
                                    >
                                        {percentage}
                                    </StyledTableSubHeaderCell>
                                );
                            })}
                        </TableRow>
                    )}
                </>
            );
        }

        // For Norm Alert with subtotals
        if (['Norm Alert'].includes(type) && weeklySubtotals) {
            const weekKeys = Object.keys(weeklySubtotals || {});

            return (
                <>
                    <TableRow>
                        {selectedColumns.map((col, index) => (
                            col.label !== displayColumn && (
                                <StyledTableSubHeaderCell
                                    key={`sub-header-${col.label}`}
                                    sx={{
                                        ...(index === 0 ? StyledLeftEmptyCell : StyledEmptyCell),
                                        position: 'sticky',
                                        top: '36px',
                                        zIndex: 1
                                    }}
                                />
                            )
                        ))}
                        {weekKeys.map((week) => (
                            <StyledTableSubHeaderCell
                                key={`week-header-${week}`}
                                sx={{
                                    position: 'sticky',
                                    top: '36px',
                                    zIndex: 1,
                                    backgroundColor: getParentChildColor(1) + ' !important',
                                    color: getTextColor(1) + ' !important',
                                    border: getParentChildColor(1) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                                }}
                            >
                                {week}
                            </StyledTableSubHeaderCell>
                        ))}
                    </TableRow>
                    <TableRow>
                        {selectedColumns.map((col, index) => (
                            col.label !== displayColumn && (
                                <StyledTableSubHeaderCell
                                    key={`subtotal-empty-${col.label}`}
                                    sx={{
                                        ...(index === 0 ? StyledLeftEmptyCell : StyledEmptyCell),
                                        position: 'sticky',
                                        top: '64px',
                                        zIndex: 1
                                    }}
                                />
                            )
                        ))}
                        {weekKeys.map((week) => {
                            const subtotalValue = weeklySubtotals?.[week] ?? "-";
                            return (
                                <StyledTableSubHeaderCell
                                    key={`subtotal-week-${week}`}
                                    type={type}
                                    sx={{
                                        position: 'sticky',
                                        top: '64px',
                                        zIndex: 1
                                    }}
                                >
                                    {subtotalValue}
                                </StyledTableSubHeaderCell>
                            )
                        })}
                    </TableRow>
                </>
            )
        }


        // Default for Norm Alert, Weekly OR, and RMPM Alert (non-MAI View)
        return (
            <TableRow>
                {selectedColumns.map((col, index) => (
                    (col.label !== displayColumn) && (
                        <StyledTableSubHeaderCell
                            key={`sub-header-default-${col.label}`}
                            sx={{
                                ...(index === 0 ? StyledLeftEmptyCell : StyledEmptyCell),
                                position: 'sticky',
                                top: '36px',
                                zIndex: 1
                            }}
                        />
                    )
                ))}
                {weekKeys.map((week) => (
                    <StyledTableSubHeaderCell
                        key={`week-default-${week}`}
                        sx={{
                            position: 'sticky',
                            top: '36px',
                            zIndex: 1,
                            backgroundColor: getParentChildColor(1) + ' !important',
                            color: getTextColor(1) + ' !important',
                            border: getParentChildColor(1) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                        }}
                    >
                        {week}
                    </StyledTableSubHeaderCell>
                ))}
            </TableRow>
        );
    };

    const renderTableBody = () => {
        if (sortedData.length === 0) {
            return (
                <StyledTableRow key="Data not available">
                    <StyledTableCell colSpan={10} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        {loading ? "Loading..." : "Data not available..."}
                    </StyledTableCell>
                </StyledTableRow>
            );
        }

        return getDataSlice()
            .map((alert, index) => {
                const rowKey = getRowKey(alert, index);
                const nonCoverageColumns = selectedColumns.filter(col => !["", "Physical FG Coverage", "Total FG Coverage"].includes(col.label));

                const renderBaseCells = () => nonCoverageColumns.map((col) => {
                    const cellKey = `${rowKey}-${col.label}`;
                    const isDescriptionColumn = [
                        "Material description",
                        "Material Desc",
                        "Basepack description",
                        "CBU description",
                        "Site cluster"
                    ].includes(col.label);

                    if (isDescriptionColumn) {
                        const isExpanded = expandedColumns[col.label];
                        return (
                            <StyledTableCell
                                className={!isExpanded ? styles.ellipsisText : ''}
                                key={cellKey}
                                sx={{
                                    minWidth: isExpanded ? '250px' : undefined,
                                    maxWidth: isExpanded ? '250px' : undefined,
                                    width: isExpanded ? '250px' : undefined,
                                    whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                    overflow: isExpanded ? 'visible' : 'hidden',
                                    textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                }}
                            >
                                <CustomTooltip title={formatText(alert[col.label])}>
                                    <span>{formatText(alert[col.label])}</span>
                                </CustomTooltip>
                            </StyledTableCell>
                        );
                    }

                    return (
                        <StyledTableCell key={cellKey}>
                            {typeof alert[col.label] === 'object' ? "-" : alert[col.label]}
                        </StyledTableCell>
                    );
                });

                const renderFgCoverageCells = () => (
                    <>
                        {weekPhysicalKeys.map((week) => {
                            const physicalData = alert["Physical FG Coverage"]?.[week];
                            const percentage = physicalData?.Percentage ?? "-";
                            return (
                                <StyledTableCell key={`physical-perc-${rowKey}-${week}`} stockcoverage={percentage} type="Physical FG Coverage">
                                    {percentage}
                                </StyledTableCell>
                            );
                        })}
                        {weekKeys.map((week) => {
                            const weeklyData = alert["Total FG Coverage"]?.[week];
                            const percentage = weeklyData?.Percentage ?? "-";
                            return (
                                <StyledTableCell key={`weekly-perc-${rowKey}-${week}`} stockcoverage={percentage} type="FG Coverage">
                                    {percentage}
                                </StyledTableCell>
                            );
                        })}
                    </>
                );

                const renderDefaultWeekCells = () => weekKeys.map((week) => {
                    const stockValue = alert[displayColumn]?.[week];
                    const tooltipContent = (
                        <div className={styles.customTooltip}>
                            <p><span className={styles.label}>Site Code :</span>{alert['Site code']}</p>
                            <p><span className={styles.label}>Material ID :</span>{alert['Material ID']}</p>
                            <p><span className={styles.label}>Safety Time(Days) :</span> {alert['Safety norm']}</p>
                            <p><span className={styles.label}>Safety Stock :</span>{alert['Safety Stock']}</p>
                            <p><span className={styles.label}>Projected Demand:</span>{alert['Projected Demand']?.[week] ?? '-'}</p>
                            <p><span className={styles.label}>Projected Supply:</span>{alert['Projected Supply']?.[week] ?? '-'}</p>
                        </div>
                    );

                    return (
                        <StyledTableCell key={`${rowKey}-${week}`} stockcoverage={stockValue} type={type}>
                            {renderNormStockCellContent(stockValue, tooltipContent)}
                        </StyledTableCell>
                    );
                });

                return (
                    <StyledTableRow key={rowKey}>
                        {renderBaseCells()}
                        {type === "FG Coverage" ? renderFgCoverageCells() : renderDefaultWeekCells()}
                    </StyledTableRow>
                );
            });
    };

    return (
        <>
            <Box sx={{ position: "relative" }}>
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 20,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <TableContainer 
                    className={`${styles.table} ${styles.pageScrollTable} ${type === 'FG Coverage' ? styles.fgCoverageTableHeight : styles.stockToNormTableHeight}`}
                    style={{ height: "auto" }}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '7px',
                            height: '7px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '7px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '7px',
                        }
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            {renderMainHeader()}
                            {renderWeekHeaders()}
                        </TableHead>
                        <TableBody>
                            {renderTableBody()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {
                ['Weekly OR', 'RMPM Alert'].includes(type) &&
                <div className={styles.contractLegends}>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#FF9998" }}></span>
                        <span>{percentageMsg?.red}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#FFEC9A" }}></span>
                        <span>{percentageMsg?.yellow}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#A3E1D2" }}></span>
                        <span>{percentageMsg?.green}</span>
                    </div>
                </div>
            }
            {
                (type === 'FG Coverage' || type === 'Physical FG Coverage') &&
                <div className={styles.contractLegends}>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#FF9998" }}></span>
                        <span>{'< 60%'}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#FFEC9A" }}></span>
                        <span>61% - 80%</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#A3E1D2" }}></span>
                        <span>81% - 100%</span>
                    </div>
                </div>
            }
            <Popover
                open={Boolean(columnInfoAnchor)}
                anchorEl={columnInfoAnchor}
                onClose={handleCloseColumnInfo}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Typography sx={{ p: 1.5, fontSize: '12px', maxWidth: '420px', whiteSpace: 'normal' }}>
                    {columnInfoText}
                </Typography>
            </Popover>
            <AlertPagination
                paginationObj={paginationObj}
                onPageChange={onPageChange}
                localPage={page}
                rowsPerPage={rowsPerPage}
                totalRecords={sortedData.length}
                onChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
        </>
    )
};

NormAlertTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columnList: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            visibility: PropTypes.bool,
            sortable: PropTypes.bool,
        })
    ),
    type: PropTypes.string,
    viewType: PropTypes.string,
    paginationObj: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        total: PropTypes.number,
        total_pages: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    onPageChange: PropTypes.func,
    loading: PropTypes.bool,
    perWeekMai: PropTypes.object,
    subtotalFgCoverage: PropTypes.object,
    subtotalPhysicalCoverage: PropTypes.object,
    weeklySubtotals: PropTypes.object,
};

NormAlertTable.defaultProps = {
    data: [],
    columnList: [],
    type: "",
    viewType: "",
    paginationObj: null,
    onPageChange: null,
    loading: false,
    perWeekMai: {},
    subtotalFgCoverage: {},
    subtotalPhysicalCoverage: {},
    weeklySubtotals: null,
};

export default NormAlertTable;